import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParentalControls, ParentalSection } from '../../../contexts/ParentalControlsContext';
import { useAuth } from '../../../contexts/AuthContext';
import PasswordModal, { PasswordModalMode } from '../../../components/PasswordModal/PasswordModal';
import './ParentalControlsSection.css';

interface ParentalControlsSectionProps {
  focused: boolean;
  /** False while section password gate is pending — toggles stay visible but disabled */
  interactive?: boolean;
  onUnlocked?: () => void;
}

type PendingSetPasswordAction = 'enableParental' | 'enableKids' | null;

interface SectionRow {
  section: ParentalSection;
  blockKey: string;
}

const ROWS: SectionRow[] = [
  { section: 'liveTV',  blockKey: 'parental.blockLiveTV'  },
  { section: 'movies',  blockKey: 'parental.blockMovies'  },
  { section: 'series',  blockKey: 'parental.blockSeries'  },
  { section: 'sports',  blockKey: 'parental.blockSports'  },
  { section: 'anime',   blockKey: 'parental.blockAnime'   },
  { section: 'kids',    blockKey: 'parental.blockKids'    },
];

const ParentalControlsSection: React.FC<ParentalControlsSectionProps> = ({
  focused,
  interactive = true,
  onUnlocked,
}) => {
  const { t } = useTranslation();
  const { authData } = useAuth();
  const {
    state,
    hasPassword, setPassword, changePassword,
    setEnabled, setKidsMode, setSectionBlocked,
  } = useParentalControls();

  const hasAccounts = Boolean(authData);

  const [pwMode, setPwMode] = useState<PasswordModalMode | null>(null);
  const [pwError, setPwError] = useState('');
  const [pendingSetPassword, setPendingSetPassword] = useState<PendingSetPasswordAction>(null);

  const sectionRef = useRef<HTMLDivElement>(null);
  const parentalRowRef = useRef<HTMLDivElement>(null);
  const kidsRowRef = useRef<HTMLDivElement>(null);

  const openPwModal = (mode: PasswordModalMode, pending: PendingSetPasswordAction = null) => {
    if (!interactive) return;
    setPwError('');
    setPendingSetPassword(pending);
    setPwMode(mode);
  };

  const focusParentalRow = () => setTimeout(() => parentalRowRef.current?.focus(), 0);

  useEffect(() => {
    if (focused) focusParentalRow();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focused]);

  const handlePwConfirm = (raw: string) => {
    if (!pwMode) return;

    if (pwMode === 'set') {
      setPassword(raw);
      onUnlocked?.();
      if (pendingSetPassword === 'enableParental') {
        setEnabled(true);
      } else if (pendingSetPassword === 'enableKids') {
        setKidsMode(true);
      }
      setPendingSetPassword(null);
      setPwMode(null);
      focusParentalRow();
      return;
    }

    if (pwMode === 'change') {
      const [oldPass, newPass] = raw.split(':::');
      const ok = changePassword(oldPass, newPass);
      if (!ok) { setPwError(t('parental.wrongPassword')); return; }
      setPwMode(null);
      focusParentalRow();
    }
  };

  const handleParentalToggle = () => {
    if (!hasAccounts || !interactive) return;
    if (state.enabled) {
      setEnabled(false);
      return;
    }
    if (!hasPassword()) {
      openPwModal('set', 'enableParental');
    } else {
      setEnabled(true);
    }
  };

  const handleKidsToggle = () => {
    if (!hasAccounts || !interactive) return;
    if (state.kidsMode) {
      setKidsMode(false);
      return;
    }
    if (!hasPassword()) {
      openPwModal('set', 'enableKids');
    } else {
      setKidsMode(true);
    }
  };

  const isActive = state.enabled;
  const isKids = state.kidsMode;
  const showSectionBlocks = isActive && !isKids;
  const rowClass = (selected: boolean) =>
    ['settings-radio-row', selected ? 'settings-radio-row--selected' : '', !interactive ? 'settings-radio-row--disabled' : '']
      .filter(Boolean)
      .join(' ');

  if (!hasAccounts) {
    return (
      <div className="settings-section parental--disabled" ref={sectionRef} tabIndex={-1}>
        <p className="parental__no-accounts">{t('parental.disabledNoAccounts')}</p>
      </div>
    );
  }

  return (
    <div className="settings-section parental" ref={sectionRef} tabIndex={-1}>
      <h2 className="settings-section__title">{t('parental.title')}</h2>

      {!interactive && (
        <p className="parental__locked-hint">{t('parental.enterPasswordToEdit')}</p>
      )}

      {/* Controle parental — independente do Modo Kids */}
      <h3 className="parental__subsection-title">{t('parental.enable')}</h3>
      <div className="settings-card">
        <div
          ref={parentalRowRef}
          className={rowClass(isActive)}
          role="checkbox"
          aria-checked={isActive}
          aria-disabled={!interactive}
          tabIndex={interactive ? 0 : -1}
          onClick={handleParentalToggle}
          onKeyDown={(e) => { if (interactive && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); handleParentalToggle(); } }}
        >
          <div className={`settings-radio-dot${isActive ? ' settings-radio-dot--selected' : ''}`}>
            {isActive && <div className="settings-radio-dot__inner" />}
          </div>
          <div className="settings-radio-label">
            <p className="settings-radio-label__title">{t('parental.enable')}</p>
            <p className="settings-radio-label__desc">{t('parental.enableDesc')}</p>
          </div>
        </div>
      </div>

      {/* Modo Kids — SEMPRE visível, independente do controle parental */}
      <h3 className="parental__subsection-title">{t('parental.kidsMode')}</h3>
      <div className="settings-card parental__kids-card">
        <div
          ref={kidsRowRef}
          className={rowClass(isKids)}
          role="checkbox"
          aria-checked={isKids}
          aria-disabled={!interactive}
          tabIndex={interactive ? 0 : -1}
          onClick={handleKidsToggle}
          onKeyDown={(e) => { if (interactive && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); handleKidsToggle(); } }}
        >
          <div className={`settings-radio-dot${isKids ? ' settings-radio-dot--selected' : ''}`}>
            {isKids && <div className="settings-radio-dot__inner" />}
          </div>
          <div className="settings-radio-label">
            <p className="settings-radio-label__title">{t('parental.kidsMode')}</p>
            <p className="settings-radio-label__desc">
              {isKids ? t('parental.kidsModeActiveMsg') : t('parental.kidsModeInactiveMsg')}
            </p>
          </div>
        </div>
      </div>

      {/* Bloqueio por seção — só com parental on + kids off */}
      {showSectionBlocks && (
        <>
          <h3 className="parental__subsection-title">{t('parental.blockSectionsTitle')}</h3>
          <div className="settings-card">
            {ROWS.map((row) => {
              const checked = state.blockedSections[row.section];
              return (
                <div
                  key={row.section}
                  className={rowClass(checked)}
                  role="checkbox"
                  aria-checked={checked}
                  aria-disabled={!interactive}
                  tabIndex={interactive ? 0 : -1}
                  onClick={() => interactive && setSectionBlocked(row.section, !checked)}
                  onKeyDown={(e) => {
                    if (interactive && (e.key === 'Enter' || e.key === ' ')) {
                      e.preventDefault();
                      setSectionBlocked(row.section, !checked);
                    }
                  }}
                >
                  <div className={`settings-radio-dot${checked ? ' settings-radio-dot--selected' : ''}`}>
                    {checked && <div className="settings-radio-dot__inner" />}
                  </div>
                  <div className="settings-radio-label">
                    <p className="settings-radio-label__title">{t(row.blockKey)}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Senha compartilhada */}
      <h3 className="parental__subsection-title">{t('parental.passwordSection')}</h3>
      <div className="parental__pw-row">
        {!hasPassword() && (
          <button
            className="parental__btn parental__btn--secondary"
            disabled={!interactive}
            onClick={() => openPwModal('set')}
            tabIndex={interactive ? 0 : -1}
          >
            {t('parental.addPassword')}
          </button>
        )}
        {hasPassword() && (
          <button
            className="parental__btn parental__btn--secondary"
            disabled={!interactive}
            onClick={() => openPwModal('change')}
            tabIndex={interactive ? 0 : -1}
          >
            {t('parental.changePassword')}
          </button>
        )}
      </div>

      {pwMode && pwMode !== 'verify' && (
        <PasswordModal
          mode={pwMode}
          onConfirm={handlePwConfirm}
          onCancel={() => { setPwMode(null); setPendingSetPassword(null); focusParentalRow(); }}
          error={pwError}
        />
      )}
    </div>
  );
};

export default ParentalControlsSection;
